import { makeAutoObservable, runInAction } from 'mobx';
import { PLATFORMS, PLATFORM_ORDER, IOS_PLATFORM_IDS } from '../data/platforms/index';
import { fileToImageBitmap, resizeImageToBlob, blobToDataUrl, getImageDimensions } from '../utils/imageTransforms';
import { downloadAllZip, downloadPlatformZip, downloadSingleIcon } from '../utils/zipExport';

class AppIconMakerStore {
  // Source image
  sourceFile = null;
  sourcePreview = null;
  sourceDimensions = { width: 0, height: 0 };
  sourceWarning = null;

  // Platform selection — default: iPhone + iPad + Android
  selectedPlatforms = new Set(['iphone', 'ipad', 'android']);

  // Export options
  exportMode = 'crop';
  bgColor = '#ffffff';
  padding = 0;
  preserveTransparency = true;
  appName = 'AppIcon';
  zipName = 'app-icons';

  // Android-specific custom filename (base name without extension)
  androidFilename = 'ic_launcher';

  // Generation state
  isGenerating = false;
  progress = 0;
  generatedIcons = [];
  error = null;

  // Preview state
  previewPlatform = 'all';
  zoomedIcon = null;

  constructor() {
    makeAutoObservable(this);
  }

  // ─── Computed ────────────────────────────────────────────────────────────────

  get totalIconCount() {
    let count = 0;
    for (const id of this.selectedPlatforms) {
      const p = PLATFORMS[id];
      if (p) count += p.icons.length;
    }
    return count;
  }

  get canGenerate() {
    return this.sourceFile !== null && this.selectedPlatforms.size > 0;
  }

  get filteredIcons() {
    if (this.previewPlatform === 'all') return this.generatedIcons;
    return this.generatedIcons.filter((i) => i.platformId === this.previewPlatform);
  }

  // ─── Source Image ─────────────────────────────────────────────────────────

  async setSourceFile(file) {
    if (this.sourcePreview) URL.revokeObjectURL(this.sourcePreview);
    runInAction(() => {
      this.sourceFile = file;
      this.sourcePreview = URL.createObjectURL(file);
      this.sourceWarning = null;
      this.generatedIcons = [];
      this.error = null;
    });
    try {
      const dims = await getImageDimensions(file);
      runInAction(() => {
        this.sourceDimensions = dims;
        if (dims.width < 512 || dims.height < 512) {
          this.sourceWarning = `Image is ${dims.width}×${dims.height}px — 1024×1024 or larger recommended for best quality.`;
        } else if (dims.width !== dims.height) {
          this.sourceWarning = `Image is not square (${dims.width}×${dims.height}). It will be center-cropped based on your export mode.`;
        }
      });
    } catch {
      runInAction(() => { this.sourceWarning = 'Could not read image dimensions.'; });
    }
  }

  clearSource() {
    if (this.sourcePreview) URL.revokeObjectURL(this.sourcePreview);
    this.sourceFile = null;
    this.sourcePreview = null;
    this.sourceDimensions = { width: 0, height: 0 };
    this.sourceWarning = null;
    this.generatedIcons = [];
    this.error = null;
  }

  // ─── Platform Selection ───────────────────────────────────────────────────

  togglePlatform(id) {
    if (this.selectedPlatforms.has(id)) {
      this.selectedPlatforms.delete(id);
    } else {
      this.selectedPlatforms.add(id);
    }
    this.generatedIcons = [];
  }

  selectAllPlatforms() {
    PLATFORM_ORDER.forEach((id) => this.selectedPlatforms.add(id));
    this.generatedIcons = [];
  }

  deselectAllPlatforms() {
    this.selectedPlatforms.clear();
    this.generatedIcons = [];
  }

  // ─── Options ──────────────────────────────────────────────────────────────

  setOption(key, value) {
    this[key] = value;
    this.generatedIcons = [];
  }

  setAndroidFilename(name) {
    this.androidFilename = name.trim() || 'ic_launcher';
  }

  // ─── Generation ──────────────────────────────────────────────────────────

  async generate() {
    if (!this.canGenerate) return;

    runInAction(() => {
      this.isGenerating = true;
      this.progress = 0;
      this.generatedIcons = [];
      this.error = null;
    });

    try {
      const bitmap = await fileToImageBitmap(this.sourceFile);
      const options = {
        exportMode: this.exportMode,
        bgColor: this.bgColor,
        padding: this.padding,
        preserveTransparency: this.preserveTransparency,
      };

      const results = [];
      const platformIds = [...this.selectedPlatforms];
      const total = platformIds.reduce((s, id) => s + (PLATFORMS[id]?.icons.length ?? 0), 0);
      let done = 0;
      const androidBase = this.androidFilename;

      for (const platformId of platformIds) {
        const platform = PLATFORMS[platformId];
        if (!platform) continue;

        for (const iconDef of platform.icons) {
          const blob = await resizeImageToBlob(bitmap, iconDef.size, options);
          const dataUrl = await blobToDataUrl(blob);

          // For Android, apply custom filename (preserve round/playstore variants)
          let filename = iconDef.filename;
          if (platformId === 'android') {
            filename = iconDef.filename
              .replace('ic_launcher_round', `${androidBase}_round`)
              .replace('ic_launcher-playstore', `${androidBase}-playstore`)
              .replace('ic_launcher', androidBase);
          }

          const zipFolder = iconDef.folder ?? platform.folder;

          results.push({
            platformId,
            platformLabel: platform.label,
            filename,
            size: iconDef.size,
            label: iconDef.label,
            blob,
            dataUrl,
            zipFolder,
            // Keep original iconDef for Contents.json
            iconDef: { ...iconDef, filename },
          });

          done++;
          const pct = Math.round((done / total) * 100);
          runInAction(() => { this.progress = pct; });
        }
      }

      bitmap.close?.();

      runInAction(() => {
        this.generatedIcons = results;
        this.isGenerating = false;
        this.progress = 100;
        this.previewPlatform = 'all';
      });
    } catch (err) {
      runInAction(() => {
        this.error = err.message || 'Generation failed. Please try again.';
        this.isGenerating = false;
      });
    }
  }

  // ─── Download ─────────────────────────────────────────────────────────────

  async downloadAll() {
    const selectedPlatformMap = {};
    for (const id of this.selectedPlatforms) {
      selectedPlatformMap[id] = PLATFORMS[id];
    }
    await downloadAllZip(this.generatedIcons, {
      zipName: this.zipName,
      appName: this.appName,
      platforms: selectedPlatformMap,
    });
  }

  async downloadPlatform(platformId) {
    // For iphone/ipad, download together as one iOS zip
    if (IOS_PLATFORM_IDS.includes(platformId)) {
      const iosIcons = this.generatedIcons.filter((i) => IOS_PLATFORM_IDS.includes(i.platformId));
      const iosPlats = {};
      for (const id of IOS_PLATFORM_IDS) {
        if (this.selectedPlatforms.has(id)) iosPlats[id] = PLATFORMS[id];
      }
      await downloadPlatformZip(iosIcons, 'ios', {
        zipName: this.zipName,
        appName: this.appName,
        platforms: iosPlats,
        isIosBundle: true,
      });
    } else {
      await downloadPlatformZip(this.generatedIcons, platformId, {
        zipName: this.zipName,
        appName: this.appName,
        platforms: { [platformId]: PLATFORMS[platformId] },
      });
    }
  }

  downloadSingleIcon(icon) {
    downloadSingleIcon(icon);
  }

  // ─── Preview ──────────────────────────────────────────────────────────────

  setPreviewPlatform(id) { this.previewPlatform = id; }
  setZoomedIcon(icon) { this.zoomedIcon = icon; }
  clearZoom() { this.zoomedIcon = null; }
}

export const appIconMakerStore = new AppIconMakerStore();
